// Customer reads. Every function takes the Supabase client as a parameter and
// has no side effects. Browser-facing callers pass a client carrying the
// user's JWT so RLS applies; partner endpoints (trailers) pass the
// service-role client and see every row.
import type { SupabaseClient } from "npm:@supabase/supabase-js@2";

export interface CustomerScacOption {
  short_code: string;
  organization_name: string;
}

/**
 * Every customer's short code (SCAC) with its organization name, ordered by
 * short code. `customers.short_code` is NOT NULL UNIQUE, so the list needs no
 * de-duplication. Used by the yard check-in form's SCAC combobox.
 */
export async function listCustomerScacOptions(db: SupabaseClient): Promise<CustomerScacOption[]> {
  const { data, error } = await db
    .from("customers")
    .select("short_code, organization_name")
    .order("short_code");
  if (error) throw new Error(`customers read failed: ${error.message}`);
  return (data as CustomerScacOption[] | null) ?? [];
}

/**
 * The Onramp org id of the customer with this short code, in this tenant.
 * Null when no customer has the short code or it has no org in the tenant.
 * `customers.short_code` is unique and customer_tenant_org_ids is unique on
 * (customer_id, tenant_id), so there is at most one answer.
 */
export async function findOnrampOrgIdByShortCode(
  db: SupabaseClient,
  tenantId: string,
  shortCode: string,
): Promise<string | null> {
  const { data: customer, error: custErr } = await db.from("customers").select("id")
    .eq("short_code", shortCode.trim()).maybeSingle();
  if (custErr) throw new Error(`customers read failed: ${custErr.message}`);
  if (!customer) return null;
  const { data: mapping, error: orgErr } = await db.from("customer_tenant_org_ids").select("onramp_org_id")
    .eq("tenant_id", tenantId).eq("customer_id", (customer as { id: string }).id).maybeSingle();
  if (orgErr) throw new Error(`customer_tenant_org_ids read failed: ${orgErr.message}`);
  return (mapping as { onramp_org_id: string } | null)?.onramp_org_id ?? null;
}

export interface CustomerOrgMatch {
  customer_id: string;
  tenant_id: string;
}

/**
 * Customers whose Onramp org id is `orgId`, in any tenant, at most two (two
 * means ambiguous). Rows store the id with or without a leading "@", so both
 * spellings match.
 */
export async function findCustomersByOnrampOrgId(db: SupabaseClient, orgId: string): Promise<CustomerOrgMatch[]> {
  const spellings = [...new Set([orgId, orgId.startsWith("@") ? orgId.slice(1) : `@${orgId}`])];
  const { data, error } = await db.from("customer_tenant_org_ids").select("customer_id, tenant_id")
    .in("onramp_org_id", spellings).limit(2);
  if (error) throw new Error(`customer_tenant_org_ids read failed: ${error.message}`);
  return (data as CustomerOrgMatch[] | null) ?? [];
}
